import React, { Suspense, useState } from 'react';
import { Loading } from '@wayste/sour-ui';
import { masterServiceAgreementDetails } from '../../../utils/_contract-utils';
import Loader from '../../Loader';
import { Paths, officeTelDisplay, supportEmail, vendorEmail } from '../../_constants';
import { APIService } from '../API/ApiService';
import SimpleAlert from '../_shared/SimpleAlert/SimpleAlert';
import type { FormProps } from './hauler-signup-form';

const HaulerSignUpForm = React.lazy(() => import('./hauler-signup-form'));

const HaulerSignUp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);

    const handleSubmit = async (data: FormProps) => {
        setIsLoading(true);

        const yardAddressString = `${data.yardAddress.addressLine1}${
            data.yardAddress.addressLine2 ? ' ' + data.yardAddress.addressLine2 + ',' : ''
        } ${data.yardAddress.city}, ${data.yardAddress.state} ${data.yardAddress.zip}`;

        let ipDetails = null;

        try {
            const ipDetailsResponse = await fetch('https://extreme-ip-lookup.com/json/');
            ipDetails = await ipDetailsResponse.json();
        } catch (error) {
            console.warn('error getting id details');
        }

        setIsLoading(true);
        const trimmedEmail = data.email.trim().toLowerCase();
        const htmlMessage = `Company: ${data.company}<br />Name: ${data.firstName} ${
            data.lastName
        }<br />Email: ${trimmedEmail}<br />Phone: ${
            data.phoneNumber
        }<br />Yard address: ${yardAddressString}<br />Sizes carried: ${data.sizesCarried.join(
            ', ',
        )}<br />Materials: ${data.materials.join(', ')} ${data.materialsOther}<br />Live loads: ${
            data.liveLoads
        }<br />Small trucks: ${data.smallTrucks}<br />Lead time: ${
            data.rollOffLeadTime
        }<br />Ancillary services: ${data.ancillaryServices.join(', ')} ${
            data.ancillaryServicesOther
        }<br />Additional notes: ${data.additionalNotes}<br />Agreed to TOS: ${data.tosAgreed}<br />TOS details: version ${
            masterServiceAgreementDetails.version
        }, updated ${
            masterServiceAgreementDetails.lastUpdated
        }, date submitted ${new Date().toISOString()}, IP Details ${JSON.stringify(ipDetails)}`;
        const textMessage = htmlMessage.split('<br />').join('\n\n');
        try {
            await APIService.Email.sendEmail({
                endPoint: 'send-email',
                emailData: {
                    toAddress: vendorEmail,
                    sender: supportEmail,
                    subject: 'Wayste Hauler Sign Up Form',
                    replyToAddress: trimmedEmail,
                    htmlMessage: htmlMessage,
                    textMessage: textMessage,
                },
            });
        } catch (error) {
            setShowErrorAlert(true);
        }
        setShowSuccessAlert(true);
        setIsLoading(false);
    };

    return (
        <>
            <Loader isLoading={isLoading} />
            <SimpleAlert
                open={showErrorAlert}
                closeDialog={() => setShowErrorAlert(false)}
                severity="error"
                title="An Error Occurred"
                msg={`An error has occurred, please try again or contact us at ${officeTelDisplay}`}
            />
            <SimpleAlert
                open={showSuccessAlert}
                closeDialog={() => {
                    window.location.href = Paths.home;
                    setShowSuccessAlert(false);
                }}
                severity="success"
                title="Successfully Submitted!"
                msg="Someone will be in touch with you shortly."
                rightButton={{
                    text: 'Okay',
                    onClick: () => {
                        window.location.href = Paths.home;
                    },
                }}
            />
            <Suspense
                fallback={
                    <div className="flex h-[500px] w-full flex-col items-center justify-center">
                        <Loading aria-label="Loading form..." />
                        <p>Loading...</p>
                    </div>
                }
            >
                <HaulerSignUpForm onSubmit={handleSubmit} />
            </Suspense>
        </>
    );
};

export default HaulerSignUp;
